import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { StatusBadge } from '@/components/ds'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { RaiffeisenBankCredentialsForm } from '@/forms/RaiffeisenBankCredentialsForm/RaiffeisenBankCredentialsForm'
import API from '@/lib/api'

import { AvailableAccounts, BankConnectLayout, ConnectionNotice } from '../components/BankConnectLayout'

export default function RaiffeisenBankPage() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'bank',
  })

  const { data, isLoading, mutate } = useSWR<{
    data: {
      credentials: boolean
      connection: 'n/a' | 'failed' | 'success'
      availableAccounts: {
        accountId: number
        accountName: string
        accountNumber: string
        accountNumberPrefix: string | null
        bankCode: string
        iban: string
        bankBicCode: string | null
        friendlyName: string | null
        accountTypeId: string
        mainCurrency: string | null
      }[]
      knownAccounts: string[]
    }
  }>('/api/v2/raiffeisen-bank', apiFetcher)

  useSetPageHeader({
    breadcrumbs: [
      {
        href: '/bank',
        children: t('title'),
      },
      {
        href: '/bank/raiffeisen',
        children: t('raiffeisen.title'),
      },
    ],
    newButton: 'invoice',
  })

  return (
    <>
      <title>{`${t('title')}, ${t('raiffeisen.title')}`}</title>

      <BankConnectLayout
        service="raiffeisen"
        title="Propojit Raiffeisenbank"
        description="Účet propojíte certifikátem s právem číst historii a zůstatky."
        note="Propojit jde jen firemní účet, osobní účty Raiffeisenbank tuhle možnost nemají."
        steps={[
          <>V internetovém bankovnictví (ne v mobilní aplikaci) otevřete Nastavení, Správa certifikátů a zvolte Nový certifikát.</>,
          <>Certifikát pojmenujte, například "invo.space", ať víte, kde ho používáte.</>,
          <>Zadejte heslo k certifikátu.</>,
          <>U účtů, které chcete propojit, vyberte alespoň "Transakční historie" a "Účty a zůstatky".</>,
          <>Potvrďte vytvoření, stáhněte certifikát (soubor .p12) a nahrajte ho do formuláře spolu s heslem.</>,
        ]}
        accounts={
          <AvailableAccounts
            loading={isLoading}
            accounts={data?.data.availableAccounts}
            knownAccounts={data?.data.knownAccounts}
            emptyText={data?.data.credentials ? 'Certifikát zatím nezpřístupnil žádné účty.' : 'Po nahrání certifikátu se tu ukážou účty, ke kterým má přístup.'}
            onAdd={(accountId) =>
              API.post(`/api/v2/raiffeisen-bank/accounts/${accountId}`, {
                accountId,
              }).then(() => mutate())
            }
          />
        }
      >
        <ConnectionNotice connection={data?.data.connection} />
        {data?.data.credentials ? (
          <div className="space-y-2">
            <StatusBadge status="matched" label="Propojeno" />
            <p className="text-sm">{t('raiffeisen.credentials.set')}</p>
            <p className="text-xs text-muted-foreground">Níže přidejte účty, které chcete sledovat.</p>
          </div>
        ) : (
          <>
            <h2 className="mb-4 text-sm font-medium">Certifikát</h2>
            <RaiffeisenBankCredentialsForm onSuccess={() => mutate()} />
          </>
        )}
      </BankConnectLayout>
    </>
  )
}
